
const ProvidesContextEntry = require("./provides-context-entry");

const KEY_STANDARD_CURRENT_USER = "currentUser";
const KEY_STANDARD_APP_CONFIGURATION = "appConfiguration";
const KEY_STANDARD_APP_USER = "appUser";
const KEY_STANDARD_RESUME_STORY = "resumeStory";
const KEY_STANDARD_CURRENT_USER_PROFILE = "currentUserProfile";
const KEY_STANDARD_CURRENT_SUBSCRIPTION = "currentSubscription";
const KEY_STANDARD_CURRENT_USER_ACCESS = "currentUserAccess";

const LEGACY_TYPE_PREFIX = "Legacy";

const ALREADY_REPORTED = Symbol("ProvidesContextsResolver.alreadyReported");

const EXPECTED_ABSENT = Symbol("ProvidesContextsResolver.expectedAbsent");

class ProvidesContextsResolver {

  static async resolveAll({
    providesContexts, urlVars, catalog, allowUri, declaredContextNames, lookupContext, registerContext, bridge, measureLabel,
    hostEntityTypes, hardFailOnUnresolved = false, surfaceLabel, primitiveContextValue, registerValueContext,
  }) {
    const resolved = {};
    const resolvedValues = {};
    const findContext = name => resolved[name] ?? lookupContext?.(name) ?? null;
    if (hostEntityTypes === undefined || hostEntityTypes === null) {
      throw new Error(
        `ProvidesContextsResolver.resolveAll (${measureLabel ?? "unlabelled"}): hostEntityTypes was `
        + "not supplied. Pass getHostEntityTypes() — without it every host-typed context is judged "
        + "against an empty ownership list and reported as misconfigured.",
      );
    }
    const hostTypes = new Set(hostEntityTypes);
    const surface = surfaceLabel ? `Page "${surfaceLabel}"` : "This surface";
    const quietlyUnavailable = new Set(declaredContextNames ?? []);
    const markLabel = `provides-contexts:${measureLabel ?? "resolve"}`;
    const markStart = `${markLabel}:start`;
    if (typeof performance !== "undefined" && performance.mark) {
      performance.mark(markStart);
    }

    for (const [contextName, storedEntry] of Object.entries(providesContexts ?? {})) {
      if (ProvidesContextsResolver.#isElementOnlyLegacyContext(storedEntry)) {
        continue;
      }
      const entry = ProvidesContextsResolver.#repairMissingOwner(storedEntry, catalog, hostTypes);
      if (ProvidesContextEntry.isValueSource(entry)) {
        continue;
      }
      if (ProvidesContextEntry.isPrimitive(entry)) {
        const primitive = ProvidesContextsResolver.#resolvePrimitive(contextName, entry, {
          surface, measureLabel, urlVars, allowUri, hardFailOnUnresolved, primitiveContextValue, registerValueContext,
        });
        if (primitive.resolved) {
          resolvedValues[contextName] = primitive.value;
          registerValueContext(contextName, primitive.value);
        }
        continue;
      }
      if (entry?.entitySource === ProvidesContextEntry.KEY_CONTEXT
        && Object.prototype.hasOwnProperty.call(resolvedValues, entry.sourceContext)) {
        const chainMessage = primitiveContextValue.chainSourceMessage(
          surface, contextName, entry.sourceContext, resolvedValues[entry.sourceContext],
        );
        if (hardFailOnUnresolved) {
          throw new Error(chainMessage);
        }
        // eslint-disable-next-line no-console
        console.error(chainMessage + " — skipped, its elements will render empty.");
        continue;
      }
      if (!ProvidesContextsResolver.#entryIsOurs(entry, catalog, hostTypes)) {
        const notOwnedMessage = `${surface} is misconfigured: it is trying to provide context "${contextName}" `
          + `of type "${entry?.entityType}"${entry?.appName ? ` from app "${entry.appName}"` : " (no app)"} `
          + "which is not owned by this surface — the app is not installed here or the type is not a host "
          + `entity. hostEntityTypes=[${[...hostTypes].join(", ")}] catalogApps=[${Object.keys(catalog ?? {}).join(", ")}] `
          + `appsDeclaringType=[${ProvidesContextsResolver.#appsDeclaringEntityType(entry?.entityType, catalog).join(", ")}]`;
        if (hardFailOnUnresolved) {
          throw new Error(notOwnedMessage);
        }
        // eslint-disable-next-line no-console
        console.error(notOwnedMessage + " — skipped, its elements will render empty.");
        continue;
      }
      const normalized = ProvidesContextEntry.normalize(entry);
      if (normalized.entitySource === ProvidesContextEntry.KEY_CONTEXT && quietlyUnavailable.has(normalized.sourceContext)
        && !ProvidesContextsResolver.#pkMapIsComplete(findContext(normalized.sourceContext)?.entityPkUrlVars)) {
        ProvidesContextsResolver.#registerPendingChain(contextName, normalized, resolved, registerContext);
        quietlyUnavailable.add(contextName);
        continue;
      }
      const pkMap = await ProvidesContextsResolver.#resolvePkMap(
        contextName, normalized, { urlVars, catalog, allowUri, findContext, bridge },
      );
      if (pkMap === ALREADY_REPORTED || pkMap === EXPECTED_ABSENT) {
        continue;
      }
      if (!ProvidesContextsResolver.#pkMapIsComplete(pkMap)) {
        if (normalized.mayBeEmpty) {
          ProvidesContextsResolver.#registerPendingChain(contextName, normalized, resolved, registerContext);
          quietlyUnavailable.add(contextName);
          continue;
        }
        const isEmptyConstant = normalized.entitySource === ProvidesContextEntry.KEY_CONSTANT
          && (!pkMap || Object.keys(pkMap).length === 0);
        const incompleteMessage = isEmptyConstant
          ? `${surface} is misconfigured: it is trying to provide context "${contextName}" of type `
            + `"${normalized.entityType}" from source "constant" but the PK is empty.`
          : `${surface} is misconfigured: context "${contextName}" (source "${normalized.entitySource}"`
            + `${normalized.appName ? `, app "${normalized.appName}"` : ""}) did not resolve to a complete PK `
            + `(pkMap=${JSON.stringify(pkMap)}). If this is a choice the viewer has not made yet, mark the `
            + "context as may be empty — deleting it instead takes the context away from every element bound to it.";
        if (hardFailOnUnresolved) {
          throw new Error(incompleteMessage);
        }
        // eslint-disable-next-line no-console
        console.error(incompleteMessage + " — skipped, its elements will render empty.");
        continue;
      }
      const context = {
        entityType: normalized.entityType,
        entitySource: normalized.entitySource,
        entityPkUrlVars: ProvidesContextsResolver.#coercePkMap(pkMap),
      };
      if (normalized.appName) {
        context.appName = normalized.appName;
      }
      if (normalized.entitySource === ProvidesContextEntry.KEY_CONTEXT && normalized.sourceField) {
        context.sourceContext = normalized.sourceContext;
        context.sourceField = normalized.sourceField;
      }
      resolved[contextName] = context;
      registerContext(contextName, context);
    }
    if (typeof performance !== "undefined" && performance.mark) {
      performance.measure(markLabel, markStart);
    }
    return resolved;
  }


  static #resolvePrimitive(contextName, entry, {
    surface, measureLabel, urlVars, allowUri, hardFailOnUnresolved, primitiveContextValue, registerValueContext,
  }) {
    if (typeof primitiveContextValue?.resolveProvidedPrimitive !== "function" || typeof registerValueContext !== "function") {
      throw new Error(
        `ProvidesContextsResolver.resolveAll (${measureLabel ?? "unlabelled"}): ${surface} provides primitive context `
        + `"${contextName}" but the caller supplied no primitiveContextValue / registerValueContext. Pass the engine's `
        + "Helpers/PrimitiveContextValue module and a value registrar, or every string, number and boolean context "
        + "on this surface is lost.",
      );
    }
    try {
      return {
        resolved: true,
        value: primitiveContextValue.resolveProvidedPrimitive({
          owner: surface, contextName, def: entry, urlVars: urlVars ?? {}, allowUri: Boolean(allowUri),
        }),
      };
    } catch (error) {
      if (hardFailOnUnresolved) {
        throw error;
      }
      // eslint-disable-next-line no-console
      console.error(`${error.message} — skipped, its elements will render empty.`);
      return { resolved: false };
    }
  }

  static #isElementOnlyLegacyContext(entry) {
    return ProvidesContextEntry.isLegacyEntityType(entry?.entityType)
      && !entry?.entitySource
      && !entry?.appName;
  }

  static #repairMissingOwner(entry, catalog, hostTypes) {
    if (!entry || entry.appName) {
      return entry;
    }
    if (ProvidesContextEntry.isLegacyEntityType(entry.entityType)
      || ProvidesContextsResolver.#isHostEntityType(entry.entityType, hostTypes)) {
      return entry;
    }
    const declaringApps = ProvidesContextsResolver.#appsDeclaringEntityType(entry.entityType, catalog);
    if (declaringApps.length !== 1) {
      return entry;
    }
    return { ...entry, appName: declaringApps[0] };
  }

  static #appsDeclaringEntityType(entityType, catalog) {
    if (typeof entityType !== "string" || !entityType) {
      return [];
    }
    const declaringApps = [];
    for (const [appName, catalogEntry] of Object.entries(catalog ?? {})) {
      if (catalogEntry?.config?.entities?.[entityType]) {
        declaringApps.push(appName);
      }
    }
    return declaringApps;
  }

  static #entryIsOurs(entry, catalog, hostTypes) {
    if (entry?.entitySource === ProvidesContextEntry.KEY_STANDARD && entry.standardEntity === KEY_STANDARD_CURRENT_USER) {
      return true;
    }
    if (entry?.entitySource === ProvidesContextEntry.KEY_STANDARD && entry.standardEntity === KEY_STANDARD_RESUME_STORY) {
      return true;
    }
    // XXX: Ambient and app-less too — the profile half of the signed-in user,
    // XXX: keyed by the same session pk. #resolveStandard has always known how to
    // XXX: resolve it; this ownership gate did NOT, so the entry was rejected
    // XXX: before resolution ever ran and the seeded standard context came out
    // XXX: with no pk. Half a feature: the resolver arm shipped, the gate arm did
    // XXX: not. See task #116.
    if (entry?.entitySource === ProvidesContextEntry.KEY_STANDARD
      && entry.standardEntity === KEY_STANDARD_CURRENT_USER_PROFILE) {
      return true;
    }
    if (entry?.entitySource === ProvidesContextEntry.KEY_STANDARD
      && entry.standardEntity === KEY_STANDARD_CURRENT_SUBSCRIPTION) {
      return true;
    }
    if (entry?.entitySource === ProvidesContextEntry.KEY_STANDARD
      && entry.standardEntity === KEY_STANDARD_CURRENT_USER_ACCESS) {
      return true;
    }
    if (entry?.entitySource === KEY_STANDARD_CURRENT_USER) {
      return true;
    }
    if (entry?.entitySource === ProvidesContextEntry.KEY_STANDARD_ROOT) {
      return true;
    }
    if (ProvidesContextsResolver.#isHostEntityType(entry?.entityType, hostTypes)) {
      return true;
    }
    if (entry?.entitySource === ProvidesContextEntry.KEY_CONSTANT) {
      return !entry.appName || Boolean(catalog?.[entry.appName]);
    }
    return Boolean(entry?.appName && catalog?.[entry.appName]);
  }

  static #isHostEntityType(entityType, hostTypes) {
    if (typeof entityType !== "string" || !entityType) {
      return false;
    }
    if (hostTypes.has(entityType)) {
      return true;
    }
    return entityType.startsWith(LEGACY_TYPE_PREFIX)
      && hostTypes.has(entityType.slice(LEGACY_TYPE_PREFIX.length));
  }

  static async #resolvePkMap(contextName, normalized, { urlVars, catalog, allowUri, findContext, bridge }) {
    if (normalized.entitySource === ProvidesContextEntry.KEY_CONSTANT) {
      return normalized.pkVars ?? null;
    }
    if (normalized.entitySource === ProvidesContextEntry.KEY_URI) {
      return ProvidesContextsResolver.#resolveUri(contextName, normalized, urlVars, allowUri);
    }
    if (normalized.entitySource === ProvidesContextEntry.KEY_CONFIG) {
      return ProvidesContextsResolver.#resolveLegacyConfig(contextName, normalized, catalog, bridge);
    }
    if (normalized.entitySource === ProvidesContextEntry.KEY_STANDARD) {
      return ProvidesContextsResolver.#resolveStandard(contextName, normalized, catalog, bridge);
    }
    if (normalized.entitySource === ProvidesContextEntry.KEY_STANDARD_ROOT) {
      return ProvidesContextsResolver.#resolveStandardRoot(contextName, normalized, bridge);
    }
    if (normalized.entitySource === ProvidesContextEntry.KEY_CONTEXT) {
      return ProvidesContextsResolver.#resolveChained(contextName, normalized, findContext, bridge);
    }
    // eslint-disable-next-line no-console
    console.error(`resolveProvidesContexts: context "${contextName}" has unknown entitySource `
      + `"${normalized.entitySource}" — skipped, its elements will render empty`);
    return ALREADY_REPORTED;
  }

  static #resolveUri(contextName, normalized, urlVars, allowUri) {
    if (!allowUri) {
      // eslint-disable-next-line no-console
      console.error(`resolveProvidesContexts: context "${contextName}" uses the uri source, which is `
        + "not available at component level (components have no slug) — skipped");
      return ALREADY_REPORTED;
    }
    const pkMap = {};
    for (const [pkField, urlVarName] of Object.entries(normalized.pkUrlVars ?? {})) {
      pkMap[pkField] = urlVars?.[urlVarName];
    }
    return pkMap;
  }

  static async #resolveLegacyConfig(contextName, normalized, catalog, bridge) {
    const configurationId = catalog?.[normalized.appName]?.configurationId;
    if (!configurationId) {
      // eslint-disable-next-line no-console
      console.error(`resolveProvidesContexts: context "${contextName}" (config source, app `
        + `${normalized.appName}) has no tenant configurationId in the payload — skipped`);
      return ALREADY_REPORTED;
    }
    const pkValue = await bridge.readConfigurationOneEntityPk(
      normalized.appName, configurationId, normalized.configurationField,
    );
    const pkField = Object.keys(normalized.pkUrlVars ?? {})[0] ?? "id";
    return pkValue === null || pkValue === undefined ? null : { [pkField]: pkValue };
  }

  static async #resolveStandard(contextName, normalized, catalog, bridge) {
    if (normalized.standardEntity === KEY_STANDARD_CURRENT_USER) {
      const currentUserPk = bridge.getCurrentUserEntityPk(normalized.appName);
      if (!ProvidesContextsResolver.#pkMapIsComplete(currentUserPk) && bridge.isViewerSignedIn?.() === false) {
        return EXPECTED_ABSENT;
      }
      return currentUserPk;
    }
    if (normalized.standardEntity === KEY_STANDARD_CURRENT_USER_PROFILE
      || normalized.standardEntity === KEY_STANDARD_CURRENT_USER_ACCESS) {
      // UserAccess answers only for the server's viewer, which on a destination is its own user.
      const profilePk = normalized.standardEntity === KEY_STANDARD_CURRENT_USER_ACCESS
        && typeof bridge.getCurrentUserAccessPk === "function"
        ? bridge.getCurrentUserAccessPk(normalized.appName)
        : bridge.getCurrentUserEntityPk(normalized.appName);
      if (!ProvidesContextsResolver.#pkMapIsComplete(profilePk) && bridge.isViewerSignedIn?.() === false) {
        return EXPECTED_ABSENT;
      }
      return profilePk;
    }
    if (normalized.standardEntity === KEY_STANDARD_CURRENT_SUBSCRIPTION) {
      if (typeof bridge?.getCurrentSubscriptionPk !== "function") {
        // eslint-disable-next-line no-console
        console.error(`resolveProvidesContexts: context "${contextName}" (standard currentSubscription) `
          + "cannot resolve — this surface's bridge has no getCurrentSubscriptionPk — skipped");
        return ALREADY_REPORTED;
      }
      return bridge.getCurrentSubscriptionPk() ?? EXPECTED_ABSENT;
    }
    if (normalized.standardEntity === KEY_STANDARD_RESUME_STORY) {
      if (typeof bridge?.getResumeStoryPk !== "function") {
        // eslint-disable-next-line no-console
        console.error(`resolveProvidesContexts: context "${contextName}" (standard resumeStory) cannot `
          + "resolve — this surface's bridge has no getResumeStoryPk — skipped");
        return ALREADY_REPORTED;
      }
      return await bridge.getResumeStoryPk() ?? EXPECTED_ABSENT;
    }
    if (normalized.standardEntity === KEY_STANDARD_APP_CONFIGURATION) {
      const configurationId = catalog?.[normalized.appName]?.configurationId;
      if (!configurationId) {
        // eslint-disable-next-line no-console
        console.error(`resolveProvidesContexts: context "${contextName}" (standard appConfiguration, `
          + `app ${normalized.appName}) has no tenant configurationId in the payload — the app is `
          + "not installed here or this is a builder route — skipped");
        return ALREADY_REPORTED;
      }
      return { id: configurationId };
    }
    if (normalized.standardEntity === KEY_STANDARD_APP_USER) {
      const appUserPk = catalog?.[normalized.appName]?.appUserPk;
      if (!appUserPk && bridge?.isViewerSignedIn?.() === false) {
        return EXPECTED_ABSENT;
      }
      if (!appUserPk) {
        // eslint-disable-next-line no-console
        console.error(`resolveProvidesContexts: context "${contextName}" (standard appUser, app `
          + `${normalized.appName}) has no appUserPk in the payload — anonymous visitor, or the `
          + "app declares no appUser, or the host did not stamp it — skipped");
        return ALREADY_REPORTED;
      }
      return appUserPk;
    }
    // eslint-disable-next-line no-console
    console.error(`resolveProvidesContexts: context "${contextName}" names unknown standard entity `
      + `"${normalized.standardEntity}" — expected one of: ${ProvidesContextEntry.VALID_STANDARD_ENTITIES.join(", ")} `
      + "— skipped");
    return null;
  }

  static #resolveStandardRoot(contextName, normalized, bridge) {
    if (typeof bridge?.getAmbientRootPk !== "function") {
      // eslint-disable-next-line no-console
      console.error(`resolveProvidesContexts: context "${contextName}" (standardRoot "${normalized.rootName}") `
        + "cannot resolve — this surface's bridge has no getAmbientRootPk — skipped");
      return ALREADY_REPORTED;
    }
    const pkMap = bridge.getAmbientRootPk(normalized.rootName);
    if (!pkMap) {
      // eslint-disable-next-line no-console
      console.error(`resolveProvidesContexts: context "${contextName}" (standardRoot "${normalized.rootName}") `
        + "has no ambient scope on this surface — skipped, its elements will render empty");
      return ALREADY_REPORTED;
    }
    return pkMap;
  }

  static async #resolveChained(contextName, normalized, findContext, bridge) {
    const sourceContext = findContext(normalized.sourceContext);
    if (!sourceContext) {
      // eslint-disable-next-line no-console
      console.error(`resolveProvidesContexts: context "${contextName}" chains off "${normalized.sourceContext}", `
        + "which is not declared EARLIER on this page/component nor inherited from an outer scope — skipped");
      return ALREADY_REPORTED;
    }
    if (!normalized.sourceField) {
      return ProvidesContextsResolver.#coercePkMap(sourceContext.entityPkUrlVars ?? {});
    }
    const sourceAppName = sourceContext.appName ?? normalized.appName;
    return bridge.readOneEntityPk(
      sourceAppName,
      sourceContext.entityType,
      ProvidesContextsResolver.#coercePkMap(sourceContext.entityPkUrlVars ?? {}),
      normalized.sourceField,
    );
  }

  static #registerPendingChain(contextName, normalized, resolved, registerContext) {
    if (normalized.entitySource !== ProvidesContextEntry.KEY_CONTEXT || !normalized.sourceField) {
      return;
    }
    const pending = {
      entityType: normalized.entityType,
      entitySource: normalized.entitySource,
      entityPkUrlVars: {},
      sourceContext: normalized.sourceContext,
      sourceField: normalized.sourceField,
    };
    if (normalized.appName) {
      pending.appName = normalized.appName;
    }
    resolved[contextName] = pending;
    registerContext(contextName, pending);
  }

  static #pkMapIsComplete(pkMap) {
    if (!pkMap || Object.keys(pkMap).length === 0) {
      return false;
    }
    return Object.values(pkMap).every(value => value !== null && value !== undefined && value !== "");
  }

  static #coercePkMap(pkMap) {
    const coerced = {};
    for (const [pkField, value] of Object.entries(pkMap)) {
      coerced[pkField] = typeof value === "string" && /^\d+$/.test(value) ? Number(value) : value;
    }
    return coerced;
  }
}

module.exports = ProvidesContextsResolver;
